import React, { useEffect, useState } from 'react';
import { Box, Grid, IconButton, Typography, useMediaQuery, useTheme, Stack } from '@mui/material';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import useUsers from '../../context/UsersContext';
import { getIcon } from '../../utils/helper';
import { sxConfig } from './otherActivityReportPage.style';
import otherActivityReportService from '../../service/otherActivityReport.service';
import ConfirmDialogV2UI from '../Interface/ConfirmDialogV2UI';
import masterDataService from '../../service/masterData.service';
import OtherActivityReportReasonDialog from './OtherActivityReportReasonDialog';
import useParam from '../../context/ParamContext';

dayjs.extend(duration);

const OpenReportTimer = ({ startDate, color }) => {
  const [elapsedTime, setElapsedTime] = useState('');

  useEffect(() => {
    if (!startDate) {
      setElapsedTime('');
      return;
    }

    const updateTimer = () => {
      const diff = dayjs.duration(dayjs().diff(dayjs(startDate)));

      setElapsedTime(
        `${diff.hours().toString().padStart(2, '0')}:${diff.minutes().toString().padStart(2, '0')}:${diff.seconds().toString().padStart(2, '0')}`
      );
    };

    updateTimer();

    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [startDate]);

  return (
    <Typography
      sx={{
        ...sxConfig.openReportTimerText,
        color: color,
      }}
    >
      {elapsedTime}
    </Typography>
  );
};

export const OtherActivityReportPage = ({ dialog }) => {
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up('md'));
  const { getUser, user } = useUsers();
  const { params, getParam } = useParam();
  const [activities, setActivities] = useState([]);
  const [reasons, setReasons] = useState({
    interrupt: [],
    downtime: [],
  });

  const defReasonDialogData = {
    open: false,
    mode: null,
    activity: null,
  };
  const [reasonDialog, setReasonDialog] = useState(defReasonDialogData);

  const userId = dialog ? dialog?.dialogData?.userId : user.userId;

  const defaultConfirmDialogData = {
    open: false,
    message: '',
    buttons: [
      {
        label: 'Megerősít',
        variant: 'outlined',
        color: 'inherit',
        onClick: () => {},
      },
    ],
  };
  const [confirmDialogData, setConfirmDialogData] = useState(defaultConfirmDialogData);

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    getReasons();
    getParam();
  }, []);

  useEffect(() => {
    getActivities();
  }, [userId]);

  const getReasons = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .taskInterruptionReasonsToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .taskDowntimeReasonsToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray).then((resp) => {
      const aData = {
        interrupt: resp[0],
        downtime: resp[1],
      };

      setReasons(aData);
    });
  };

  const getActivities = () => {
    if (!userId) return;

    otherActivityReportService.getData({ userId }).then((data) => {
      setActivities(data);
    });
  };

  const hideConfirmDialog = () => {
    setConfirmDialogData(defaultConfirmDialogData);
  };

  const checkOpenReportByUser = ({ onConfirm }) => {
    hideConfirmDialog();
    otherActivityReportService.checkOpenReportByUser(userId).then((data) => {
      if (data.hasOpenReport) {
        setConfirmDialogData({
          open: true,
          message: 'Már van folyamatban lévő munkád. Megszakítod azt, hogy elindíthasd ezt?',
          buttons: [
            {
              label: 'Igen',
              variant: 'contained',
              color: '#27ca37',
              onClick: onConfirm,
            },
          ],
        });
      } else {
        onConfirm();
      }
    });
  };

  const handleStartJob = (data) => {
    const body = {
      otherActivityId: data.id,
      userId,
    };

    otherActivityReportService.startJob(body).then(() => {
      getActivities();
      hideConfirmDialog();

      if (dialog) {
        dialog.setRefetch(true);
      }
    });
  };

  const handleStopJob = async (data, values = {}) => {
    const body = {
      ...values,
      id: data.report.id,
      userId,
    };

    await otherActivityReportService.stopJob(body);

    getActivities();
    hideConfirmDialog();
  };

  const openConfirmDialog = (data) => {
    const isRunning = data.report?.endDate === null;
    const isDowntime = !!data.report?.downtimeCode;

    if (isRunning) {
      setConfirmDialogData({
        open: true,
        message: `Mit szeretne csinálni?`,
        buttons: [
          {
            label: 'Kész',
            variant: 'contained',
            color: '#27ca37',
            onClick: () => handleStopJob(data),
          },
          ...(params.OTHER_ACTIVITY_REPORT_HIDE_INTERRUPT !== 'Y'
            ? [
                {
                  label: 'Megszakít',
                  variant: 'contained',
                  color: '#f59e0b',
                  onClick: () => {
                    hideConfirmDialog();
                    setReasonDialog({
                      open: true,
                      mode: 'INTERRUPT',
                      activity: data,
                    });
                  },
                },
              ]
            : []),
          {
            label: isDowntime ? 'Folytatás' : 'Állásidő',
            variant: 'contained',
            color: '#494cff',
            onClick: () => {
              hideConfirmDialog();
              if (isDowntime) {
                handleStopJob(data, { createNewReport: true });
              } else {
                setReasonDialog({
                  open: true,
                  mode: 'DOWNTIME',
                  activity: data,
                });
              }
            },
          },
        ],
      });
    } else {
      setConfirmDialogData({
        open: true,
        message: `Biztosan el szeretné indítani a ${data.name} tevékenységet?`,
        buttons: [
          {
            label: 'Igen',
            variant: 'contained',
            color: '#27ca37',
            onClick: () => {
              checkOpenReportByUser({
                onConfirm: () => handleStartJob(data),
              });
            },
          },
        ],
      });
    }
  };

  const handleReasonSelect = async (code) => {
    const payload =
      reasonDialog.mode === 'INTERRUPT'
        ? { interruptionCode: code }
        : reasonDialog.mode === 'DOWNTIME'
          ? { downtimeCode: code, createNewReport: true }
          : {};

    await handleStopJob(reasonDialog.activity, payload);

    setReasonDialog(defReasonDialogData);
  };

  return (
    <>
      <Box
        sx={{
          ...sxConfig.page,
          minHeight: dialog ? '100%' : 'calc(100vh - 65px)',
          backgroundColor: dialog
            ? '#fff'
            : {
                xs: '#fff',
                md: '#e9f3fc',
              },
        }}
      >
        <Grid
          container
          spacing={{
            xs: 1.5,
            md: 2.5,
          }}
          justifyContent="center"
        >
          {activities?.map((activity) => {
            const isRunning = activity.report?.endDate === null;
            const isInterrupted = !!activity.report?.interruptionCode;
            const isDowntime = !!activity.report?.downtimeCode;
            const interruptionReason = activity.report?.taskInterruptionReason;
            const downtimeReason = activity.report?.taskDowntimeReason;

            const activeReason = isInterrupted ? interruptionReason : isDowntime ? downtimeReason : null;

            const isStopped = isInterrupted || isDowntime;

            return (
              <Grid item key={activity.id} sx={sxConfig.cardGridItem}>
                <Box
                  sx={{
                    ...sxConfig.card,
                    border: {
                      xs: `2px solid ${isStopped ? '#FFACA2' : isRunning ? '#1E88E5' : '#BFE9D2'}`,
                      md: `4px solid ${isStopped ? '#FFACA2' : isRunning ? '#1E88E5' : '#16a34a'}`,
                    },
                    backgroundColor: isStopped
                      ? '#FFD2D1'
                      : isRunning
                        ? '#56A5F7'
                        : {
                            xs: '#effaf3',
                            md: '#d8f4f8',
                          },
                    color: isStopped
                      ? 'rgba(239, 68, 68, 1)'
                      : isRunning
                        ? 'white'
                        : {
                            xs: '#000',
                            md: '#16a34a',
                          },

                    '@media (hover: hover)': {
                      '&:hover': {
                        backgroundColor: isStopped ? '#FA9E9E' : isRunning ? '#56A5F7' : '#bbf7d0',
                        transform: 'translateY(-4px) scale(1.05)',
                      },
                    },
                  }}
                  onClick={() => openConfirmDialog(activity)}
                >
                  <Typography component="div" sx={sxConfig.activityTitle}>
                    {activity.name}
                  </Typography>

                  <IconButton disableRipple tabIndex={-1} sx={sxConfig.activityIconButton}>
                    {getIcon(
                      activity.icon,
                      isLgUp ? '3rem' : '2.5rem',
                      isStopped ? 'rgba(239, 68, 68, 1)' : isRunning ? 'white' : isLgUp ? '#16a34a' : '#0fbd1d'
                    )}
                  </IconButton>

                  {isRunning && (
                    <Box sx={sxConfig.openReportTimer}>
                      <OpenReportTimer
                        startDate={activity.report.startDate}
                        color={isStopped ? 'rgba(239, 68, 68, 1)' : '#FFCA28'}
                      />
                    </Box>
                  )}

                  {activeReason && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="center"
                      sx={sxConfig.cardReasonContainer}
                    >
                      <IconButton disableRipple>
                        {getIcon(activeReason.icon, isLgUp ? '2rem' : '1.5rem', 'rgba(239, 68, 68, 1)')}
                      </IconButton>

                      <Typography sx={sxConfig.cardReasonBoxText}>{activeReason.name}</Typography>
                    </Stack>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
      <ConfirmDialogV2UI
        {...confirmDialogData}
        largeButtons={true}
        maxWidth="lg"
        fullWidth={true}
        onClose={hideConfirmDialog}
      />

      <OtherActivityReportReasonDialog
        open={reasonDialog.open}
        handleClose={() => setReasonDialog(defReasonDialogData)}
        data={reasonDialog.mode === 'INTERRUPT' ? reasons.interrupt : reasons.downtime}
        onSelect={handleReasonSelect}
      />
    </>
  );
};
